import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import { useTranslation } from "react-i18next";
import { getStorefrontLayout, updateStorefrontLayout } from "../../api/storeService";
import { EmptyState } from "../Admin/analyticsShared";

function itemKey(item, index) {
  if (item.type === "Category") return `category-${item.categoryId}`;
  return `highlight-${item.highlightType}-${index}`;
}

function iconFor(item, highlightOptions) {
  if (item.type === "Category") return LocalOfferOutlinedIcon;
  return highlightOptions.find((h) => h.value === item.highlightType)?.icon || StarRoundedIcon;
}

export default function StoreLayoutDesigner() {
  const { t } = useTranslation(["boStoreLayoutDesigner", "boStoreCarousel", "common"]);
  const { storeId } = useParams();
  const navigate = useNavigate();

  const HIGHLIGHT_OPTIONS = [
    { value: "Discounted", label: t("boStoreLayoutDesigner:discounted"), icon: SellOutlinedIcon },
    { value: "BestSellers", label: t("boStoreLayoutDesigner:bestSellers"), icon: TrendingUpRoundedIcon },
    { value: "NewArrivals", label: t("boStoreLayoutDesigner:newArrivals"), icon: AutoAwesomeRoundedIcon },
    { value: "TopRated", label: t("boStoreLayoutDesigner:topRated"), icon: StarRoundedIcon },
  ];

  const [items, setItems] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const isDirty = useMemo(() => JSON.stringify(items) !== JSON.stringify(savedItems), [items, savedItems]);

  function load() {
    setLoading(true);
    setError("");
    getStorefrontLayout(storeId)
      .then((data) => {
        setItems(data);
        setSavedItems(data);
      })
      .catch(() => setError(t("boStoreLayoutDesigner:loadError")))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [storeId]);

  const availableHighlights = useMemo(
    () => HIGHLIGHT_OPTIONS.filter((option) => !items.some((item) => item.type === "Highlight" && item.highlightType === option.value)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  function moveItem(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }

  function toggleVisible(index) {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, isVisible: !item.isVisible } : item)));
  }

  function addHighlight(option) {
    setItems((prev) => [
      ...prev,
      { type: "Highlight", highlightType: option.value, label: option.label, isVisible: true, displayOrder: prev.length },
    ]);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const payload = items.map((item) => ({
        type: item.type,
        categoryId: item.type === "Category" ? item.categoryId : null,
        highlightType: item.type === "Highlight" ? item.highlightType : null,
        isVisible: item.isVisible,
      }));
      const updated = await updateStorefrontLayout(storeId, payload);
      setItems(updated);
      setSavedItems(updated);
      setNotice(t("boStoreLayoutDesigner:saved"));
    } catch {
      setError(t("boStoreLayoutDesigner:saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(`/business-owner/stores/${storeId}`)}>
          {t("boStoreLayoutDesigner:backToStore")}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isDirty || saving}>
          {saving ? t("boShared:saving") : t("common:save")}
        </Button>
      </Stack>

      <Typography variant="h4" sx={{ mb: 0.5 }}>
        {t("boStoreLayoutDesigner:title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("boStoreLayoutDesigner:subtitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <ListItem
          sx={{ cursor: "pointer" }}
          onClick={() => navigate(`/business-owner/stores/${storeId}/carousel`)}
          secondaryAction={<ChevronRightRoundedIcon color="disabled" />}
        >
          <IconRow icon={CollectionsOutlinedIcon} title={t("boStoreCarousel:title")} subtitle={t("boStoreLayoutDesigner:carouselSubtitle")} />
        </ListItem>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState icon={LocalOfferOutlinedIcon} text={t("boStoreLayoutDesigner:noItems")} />
      ) : (
        <Card variant="outlined">
          <List disablePadding>
            {items.map((item, index) => {
              const Icon = iconFor(item, HIGHLIGHT_OPTIONS);
              return (
                <ListItem
                  key={itemKey(item, index)}
                  divider={index < items.length - 1}
                  sx={{ opacity: item.isVisible ? 1 : 0.55, py: 1.25 }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <IconButton size="small" onClick={() => moveItem(index, -1)} disabled={index === 0}>
                        <KeyboardArrowUpRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveItem(index, 1)} disabled={index === items.length - 1}>
                        <KeyboardArrowDownRoundedIcon fontSize="small" />
                      </IconButton>
                      <Switch checked={item.isVisible} onChange={() => toggleVisible(index)} size="small" />
                    </Stack>
                  }
                >
                  <IconRow
                    icon={Icon}
                    title={item.label}
                    subtitle={
                      (item.type === "Category" ? t("boStoreLayoutDesigner:category") : t("boStoreLayoutDesigner:promoShelf")) +
                      (!item.isVisible ? ` · ${t("boStoreLayoutDesigner:hidden")}` : "")
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Card>
      )}

      {availableHighlights.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1, display: "block" }}>
            {t("boStoreLayoutDesigner:addPromoShelf")}
          </Typography>
          <Stack spacing={1}>
            {availableHighlights.map((option) => (
              <Card variant="outlined" key={option.value}>
                <ListItem
                  sx={{ cursor: "pointer" }}
                  onClick={() => addHighlight(option)}
                  secondaryAction={<AddCircleOutlineRoundedIcon color="primary" />}
                >
                  <IconRow icon={option.icon} title={option.label} />
                </ListItem>
              </Card>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function IconRow({ icon: Icon, title, subtitle }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flexGrow: 1 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
